import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { Loader2, Send } from "lucide-react";
import { VideoCommentsForm, type CommentFormDefaults } from "./VideoCommentsForm";
import { VideoCommentsList } from "./VideoCommentsList";
import type { EventComment, EventCommentItem } from "../../../types";
import type { GPGamePlayerResponse, MergedGpEventResponse, TeamPlayer } from "../../../types";

interface EventCommentsPanelProps {
	mergedEvent: MergedGpEventResponse;
	comments?: EventCommentItem[];
	onUpdate: (values: EventComment) => void | Promise<void>;
	onDeleteComment: (id: number, eventId: number) => void;
	onEditComment: (id: number, data: unknown) => void;
	players: GPGamePlayerResponse[];
	isLoading?: boolean;
	commentFormDefaults?: CommentFormDefaults | null;
}

export function EventCommentsPanel({
	onUpdate,
	mergedEvent,
	comments,
	onDeleteComment,
	onEditComment,
	players,
	isLoading = false,
	commentFormDefaults,
}: EventCommentsPanelProps) {
	const [activeTab, setActiveTab] = useState<"form" | "comments">(
		mergedEvent.comments.length > 0 ? "comments" : "form"
	);
	const [isSubmittingFirstComment, setIsSubmittingFirstComment] = useState(false);

	const formRef = useRef<any>(null);
	const editFormRef = useRef<any>(null);

	useEffect(() => {
		setActiveTab(mergedEvent.comments.length > 0 ? "comments" : "form");
		if (mergedEvent.comments.length > 0) {
			setIsSubmittingFirstComment(false);
		}
	}, [mergedEvent.id, mergedEvent.comments.length]);

	const displayComments = useMemo((): EventCommentItem[] => comments ?? [], [comments]);

	const hasComments = displayComments.length > 0;

	const onCreateComment = useCallback(async (values: EventComment) => {
		const isFirstComment = mergedEvent.comments.length === 0;
		if (isFirstComment) {
			setIsSubmittingFirstComment(true);
		}
		try {
			await onUpdate(values);
		} catch {
			if (isFirstComment) {
				setIsSubmittingFirstComment(false);
			}
		}
	}, [mergedEvent.comments.length, onUpdate]);

	const filteredPlayers: TeamPlayer[] = useMemo(() => {
		if (!players || players.length === 0) {
			return [];
		}

		return players
			.filter((player) => player.teamId === mergedEvent.gameTeamId)
			.map(
				(player) =>
					({
						id: player.id,
						name: player.name,
						photo: player.photo ?? null,
					}) as TeamPlayer
			)
			.sort((a, b) => (a.id === mergedEvent.gamePlayerId ? -1 : b.id === mergedEvent.gamePlayerId ? 1 : 0));
	}, [players, mergedEvent]);

	const eventSummary = useMemo(() => {
		const totalSeconds = Math.floor(mergedEvent.lastVideoTime / 1000);
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;
		const formattedTime = hours > 0
			? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
			: `${minutes}:${String(seconds).padStart(2, "0")}`;
		const formattedTypes = mergedEvent.types
			.map((type) => `#${_(`event.v3.type.${type}` as keyof Keys).toLowerCase()}`)
			.join(" ");

		return `${mergedEvent.playerName} ${formattedTypes} · ${formattedTime}`;
	}, [mergedEvent]);

	const handlePrimaryAction = () => {
		if (!hasComments) {
			formRef.current?.submit();
			return;
		}
		if (activeTab === "form") {
			formRef.current?.submit();
			setActiveTab("comments");
		} else {
			editFormRef.current?.submit();
		}
	};

	return (
		<div className="event-comments-panel flex flex-col min-h-0 flex-1">
			<div className="px-4 pb-3 shrink-0">
				<p className="text-sm text-gray-700 font-medium leading-snug break-words">{eventSummary}</p>
			</div>
			<div className="w-full min-h-[120px] relative flex flex-col flex-1 min-h-0 px-4 pt-3 pb-0">
				{isLoading && (
					<div className={"absolute inset-0 z-10 flex flex-col gap-3 items-center rounded-lg bg-white/90"}>
						<div className="absolute h-full w-full flex items-center justify-center">
							<Loader2
								className={"h-10 w-10 text-blue-500"}
								style={{ animation: "spin 0.8s linear infinite" }}
								aria-hidden={hasComments}
							/>
						</div>
					</div>
				)}
				{hasComments ? (
					<div className="w-full flex flex-col flex-1 min-h-0">
						<div className="flex justify-between bg-gray-100 rounded-lg p-1 shrink-0 mb-4">
							<button
								type="button"
								className={`px-3 py-2 w-1/2 rounded-sm text-sm font-medium transition-all duration-200 ${activeTab === "comments"
									? "bg-white text-gray-900 shadow-sm border border-gray-200"
									: "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
									}`}
								onClick={() => setActiveTab("comments")}
							>
								{_("gamePage.event.seeComments.label")}
							</button>
							<button
								type="button"
								className={`px-3 py-2 w-1/2 rounded-sm text-sm font-medium transition-all duration-200 ${activeTab === "form"
									? "bg-white text-gray-900 shadow-sm border border-gray-200"
									: "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
									}`}
								onClick={() => setActiveTab("form")}
							>
								{_("discover.gameComment.form.comment.title")}
							</button>
						</div>

						<div className="tab-content relative flex-1 min-h-0 overflow-y-auto pb-3">
						{activeTab === "form" ? (
							<VideoCommentsForm ref={formRef} onSubmit={onCreateComment} teamPlayers={filteredPlayers} commentFormDefaults={commentFormDefaults} />
							) : (
								<VideoCommentsList
									eventId={mergedEvent.id}
									comments={displayComments}
									teamPlayers={filteredPlayers}
									onDeleteComment={onDeleteComment}
									onEditComment={onEditComment}
									editFormRef={editFormRef}
								/>
							)}
						</div>
					</div>
			) : isSubmittingFirstComment ? (
			<div className="flex-1 min-h-0 flex items-center justify-center">
				<Loader2
					className="h-10 w-10 text-blue-500"
					style={{ animation: "spin 0.8s linear infinite" }}
				/>
			</div>
			) : (
			<div className="flex-1 min-h-0 overflow-y-auto pb-3">
				<VideoCommentsForm ref={formRef} onSubmit={onCreateComment} teamPlayers={filteredPlayers} commentFormDefaults={commentFormDefaults} />
			</div>
			)}
			</div>
		{(!hasComments || activeTab === "form") && !isSubmittingFirstComment && (
			<div className="flex gap-2 justify-end p-3 border-t border-gray-200 bg-gray-50 shrink-0 mt-auto">
				<button
					type="button"
					onClick={handlePrimaryAction}
					className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-Surface-action-primary text-size-base leading-sm font-medium text-Text-primary"
				>
					<Send size={16} />
					{_("game.comment.send")}
				</button>
			</div>
		)}
		</div>
	);
}
